import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Plus, CheckCircle2, Clock, X } from "lucide-react";
import { listTasks, completeTask, snoozeTask, upsertTask } from "@/lib/crm.functions";

export const Route = createFileRoute("/_authenticated/admin/tasks")({
  component: Tasks,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="text-center py-20">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
});

const filters = [
  { k: "today", label: "Today" },
  { k: "overdue", label: "Overdue" },
  { k: "upcoming", label: "Upcoming" },
  { k: "done", label: "Done" },
  { k: "all", label: "All" },
] as const;

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Tasks() {
  const [filter, setFilter] = useState<(typeof filters)[number]["k"]>("today");
  const [open, setOpen] = useState(false);
  const load = useServerFn(listTasks);
  const complete = useServerFn(completeTask);
  const snooze = useServerFn(snoozeTask);
  const add = useServerFn(upsertTask);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["crm", "tasks", filter], queryFn: () => load({ data: { filter } }), staleTime: 15_000 });

  const mCreate = useMutation({ mutationFn: (v: any) => add({ data: v }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm"] }); setOpen(false); } });
  const mDone = useMutation({ mutationFn: (id: string) => complete({ data: { id, done: true } }), onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }) });
  const mSnooze = useMutation({ mutationFn: (v: { id: string; days: number }) => snooze({ data: v }), onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }) });

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-amber-300 text-[11px] tracking-[0.25em] uppercase font-semibold">CRM</p>
          <h1 className="font-serif text-3xl text-white mt-1">Tasks</h1>
          <p className="text-sm text-zinc-400 mt-2">Reminders and follow-ups across every contact.</p>
        </div>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-amber-300 text-zinc-950 rounded-full px-4 py-2 text-sm font-semibold hover:bg-amber-200">
          <Plus className="size-4" /> New task
        </button>
      </header>

      <div className="flex gap-1 border-b border-white/10 text-sm">
        {filters.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-2 border-b-2 ${filter === f.k ? "border-amber-300 text-white" : "border-transparent text-zinc-400 hover:text-white"}`}>{f.label}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-20"><Loader2 className="size-6 animate-spin text-amber-300" /></div>
      ) : (data ?? []).length === 0 ? (
        <p className="text-center text-zinc-500 py-16 text-sm">No tasks in this view.</p>
      ) : (
        <ul className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
          {(data ?? []).map((t: any) => (
            <li key={t.id} className="flex items-start gap-3 p-4">
              <button onClick={() => mDone.mutate(t.id)} disabled={t.status === "done"} className="mt-0.5 size-5 rounded-full border border-white/20 grid place-items-center hover:bg-amber-300/20 disabled:opacity-40">
                <CheckCircle2 className={`size-3 ${t.status === "done" ? "text-emerald-400" : "text-transparent hover:text-amber-300"}`} />
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${t.status === "done" ? "text-zinc-500 line-through" : "text-white"}`}>{t.title}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <Clock className="size-3" /> {fmt(t.due_at)}
                  {t.priority === "high" && <span className="text-amber-300">High</span>}
                  {t.crm_contacts?.name && (
                    <Link to="/admin/contacts/$id" params={{ id: t.crm_contacts.id }} className="text-amber-300 hover:underline">
                      {t.crm_contacts.name}
                    </Link>
                  )}
                </p>
              </div>
              {t.status === "open" && (
                <div className="flex items-center gap-1">
                  <button onClick={() => mSnooze.mutate({ id: t.id, days: 1 })} className="text-[11px] text-zinc-500 hover:text-amber-300 px-2">+1d</button>
                  <button onClick={() => mSnooze.mutate({ id: t.id, days: 7 })} className="text-[11px] text-zinc-500 hover:text-amber-300 px-2">+1w</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <NewTaskModal onClose={() => setOpen(false)} pending={mCreate.isPending} error={mCreate.error?.message}
          onSubmit={(v) => mCreate.mutate({ ...v, due_at: v.due_at ? new Date(v.due_at).toISOString() : null })} />
      )}
    </div>
  );
}

function NewTaskModal({ onClose, onSubmit, pending, error }: { onClose: () => void; onSubmit: (v: any) => void; pending: boolean; error?: string }) {
  const [f, setF] = useState({ title: "", due_at: "", priority: "normal", description: "" });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl text-white">New task</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="size-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (f.title.trim()) onSubmit(f); }} className="space-y-3">
          <input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
          <textarea placeholder="Description (optional)" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
          <div className="grid grid-cols-2 gap-3">
            <input type="datetime-local" value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200" />
            <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200">
              <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
            </select>
          </div>
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button disabled={pending || !f.title.trim()} className="w-full bg-amber-300 text-zinc-950 rounded-full py-2.5 text-sm font-semibold disabled:opacity-60">{pending ? "Saving…" : "Create task"}</button>
        </form>
      </div>
    </div>
  );
}